/** Auto-detected flight events — notable moments to mark and jump to. */

import type { Flight } from './model/types.ts';

export interface FlightEvent {
  id: string;
  label: string;
  icon: string;
  index: number;
  timeMs: number;
  /** The notable value at this moment (e.g. peak altitude), with its unit. */
  value?: number;
  unit?: string;
}

function argmax(a: Float64Array): number {
  let bi = 0;
  let bv = -Infinity;
  for (let i = 0; i < a.length; i++)
    if (a[i] > bv) {
      bv = a[i];
      bi = i;
    }
  return bi;
}
function argmin(a: Float64Array): number {
  let bi = 0;
  let bv = Infinity;
  for (let i = 0; i < a.length; i++)
    if (a[i] < bv) {
      bv = a[i];
      bi = i;
    }
  return bi;
}

export function detectEvents(flight: Flight): FlightEvent[] {
  const s = flight.samples;
  const t = s.time;
  const rel = s.numeric['rel_alt'];
  const sp = s.numeric['ground_speed'];
  const dist = s.numeric['dist_from_home'];
  const vs = s.numeric['vertical_speed'];
  const evs: FlightEvent[] = [];
  const push = (id: string, label: string, icon: string, index: number, value?: number, unit?: string) => {
    if (index >= 0 && index < s.length) evs.push({ id, label, icon, index, timeMs: t[index], value, unit });
  };

  // Takeoff / landing: first and last time clearly airborne.
  if (rel) {
    const airborne = (i: number) => rel[i] > 1.5;
    let first = -1;
    let last = -1;
    for (let i = 0; i < s.length; i++)
      if (airborne(i)) {
        if (first < 0) first = i;
        last = i;
      }
    push('takeoff', 'Takeoff', '🛫', first);
    // Only meaningful once the drone actually flew — otherwise argmax lands on a
    // ~0 m ground frame and shows a bogus "Max altitude: 0 m" with no takeoff.
    if (first >= 0) {
      const hi = argmax(rel);
      push('maxalt', 'Max altitude', '🏔️', hi, rel[hi], 'm');
    }
    // In-flight lowest point only (ignore pre-takeoff / post-landing ground frames).
    if (first >= 0 && last > first) {
      let loIdx = first;
      for (let i = first; i <= last; i++) if (rel[i] < rel[loIdx]) loIdx = i;
      // Worth marking only if it's a real dip below the takeoff/landing level.
      if (rel[loIdx] < Math.min(rel[first], rel[last]) - 5) push('minalt', 'Lowest point', '🔻', loIdx, rel[loIdx], 'm');
    }
    push('landing', 'Landing', '🛬', last);
  }
  if (sp) {
    const fi = argmax(sp);
    push('maxspeed', 'Top speed', '⚡', fi, sp[fi], 'm/s');
  }
  if (dist) {
    const di = argmax(dist);
    push('farthest', 'Farthest from home', '🎯', di, dist[di], 'm');
  }
  if (vs) {
    const ci = argmax(vs), de = argmin(vs);
    push('climb', 'Fastest climb', '⏫', ci, vs[ci], 'm/s');
    push('descent', 'Fastest descent', '⏬', de, vs[de], 'm/s');
  }
  // The longest GPS-loss stretch (ATTI / no satellites). Mark its onset.
  const gv = s.numeric['gps_valid'];
  if (gv) {
    let bestStart = -1, bestLen = 0, curStart = -1;
    const close = (end: number) => {
      if (curStart < 0) return;
      const len = t[end] - t[curStart];
      if (len > bestLen) { bestLen = len; bestStart = curStart; }
      curStart = -1;
    };
    for (let i = 0; i < s.length; i++) {
      if (!gv[i]) { if (curStart < 0) curStart = i; }
      else close(i);
    }
    close(s.length - 1);
    if (bestStart >= 0 && bestLen > 1000) {
      push('gpsloss', bestStart === 0 ? 'No GPS at launch' : 'GPS signal lost', '🛰️', bestStart, bestLen / 1000, 's');
    }
  }

  // De-duplicate events that land on (nearly) the same frame, keeping the first.
  const seen = new Set<number>();
  const out: FlightEvent[] = [];
  for (const e of evs.sort((a, b) => a.timeMs - b.timeMs)) {
    const bucket = Math.round(e.timeMs / 500);
    if (seen.has(bucket)) continue;
    seen.add(bucket);
    out.push(e);
  }
  return out;
}
